'use client';

import { useState } from 'react';
import {
  formatColorOutputs,
  generateShades,
  mixColors,
  parseHex,
  rgbToHex,
  type ColorVariant,
} from '@/lib/browser-color-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const badges: Record<ColorVariant, { badge: string; title: string }> = {
  'hex-to-rgb': { badge: 'HEX', title: 'Convert HEX to RGB' },
  'rgb-to-hex': { badge: 'RGB', title: 'Convert RGB to HEX' },
  'color-shades-generator': { badge: 'Shades', title: 'Generate color shades and tints' },
  'color-mixer': { badge: 'Mixer', title: 'Mix two colors' },
};

export default function BrowserColorToolkit({ variant }: { variant: ColorVariant }) {
  const meta = badges[variant];
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [hex, setHex] = useState('#6366F1');
  const [r, setR] = useState('99');
  const [g, setG] = useState('102');
  const [b, setB] = useState('241');
  const [colorA, setColorA] = useState('#6366f1');
  const [colorB, setColorB] = useState('#ec4899');
  const [ratio, setRatio] = useState(50);

  async function handleCopy(value: string) {
    try {
      await copyText(value);
      setCopyMessage(`Copied ${value}`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const parsed = parseHex(hex);
  const rgbFromInputs = { r: Number(r) || 0, g: Number(g) || 0, b: Number(b) || 0 };
  const a = parseHex(colorA);
  const bColor = parseHex(colorB);
  const mixed = a && bColor ? mixColors(a, bColor, ratio / 100) : null;
  const shadeBase = parseHex(hex);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{meta.badge}</span>
        <h2 className="tool-live-title">{meta.title}</h2>
        <p className="tool-live-subtitle">
          Results update live in your browser. Nothing is uploaded and no server is used.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'hex-to-rgb' || variant === 'color-shades-generator' ? (
              <>
                <label className="tool-live-field">
                  <span>Pick a color</span>
                  <input
                    onChange={(e) => setHex(e.target.value.toUpperCase())}
                    type="color"
                    value={parsed ? rgbToHex(parsed) : '#000000'}
                  />
                </label>
                <label className="tool-live-field">
                  <span>HEX value</span>
                  <input onChange={(e) => setHex(e.target.value)} type="text" value={hex} />
                </label>
              </>
            ) : null}
            {variant === 'rgb-to-hex' ? (
              <>
                <label className="tool-live-field">
                  <span>Red (0-255)</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setR(e.target.value)}
                    type="text"
                    value={r}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Green (0-255)</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setG(e.target.value)}
                    type="text"
                    value={g}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Blue (0-255)</span>
                  <input
                    inputMode="numeric"
                    onChange={(e) => setB(e.target.value)}
                    type="text"
                    value={b}
                  />
                </label>
              </>
            ) : null}
            {variant === 'color-mixer' ? (
              <>
                <label className="tool-live-field">
                  <span>Color A</span>
                  <input onChange={(e) => setColorA(e.target.value)} type="color" value={colorA} />
                </label>
                <label className="tool-live-field">
                  <span>Color B</span>
                  <input onChange={(e) => setColorB(e.target.value)} type="color" value={colorB} />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Mix ratio: {ratio}% toward B</span>
                  <input
                    max={100}
                    min={0}
                    onChange={(e) => setRatio(Number(e.target.value))}
                    type="range"
                    value={ratio}
                  />
                </label>
              </>
            ) : null}
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {variant === 'hex-to-rgb' && parsed ? (
            <div className="tool-html-meta-grid">
              {Object.entries(formatColorOutputs(parsed)).map(([label, value]) => (
                <div className="tool-live-field" key={label}>
                  <span>{label.toUpperCase()}</span>
                  <input onFocus={(e) => e.target.select()} readOnly type="text" value={value} />
                </div>
              ))}
            </div>
          ) : null}
          {variant === 'rgb-to-hex' ? (
            <>
              <div
                className="tool-live-preview-frame"
                style={{ height: 120, borderRadius: 12, background: rgbToHex(rgbFromInputs) }}
              />
              <div className="tool-live-field tool-live-field-full">
                <span>HEX</span>
                <input
                  onFocus={(e) => e.target.select()}
                  readOnly
                  type="text"
                  value={rgbToHex(rgbFromInputs)}
                />
              </div>
              <button
                className="button button-secondary"
                onClick={() => handleCopy(rgbToHex(rgbFromInputs))}
                type="button"
              >
                Copy HEX
              </button>
            </>
          ) : null}
          {variant === 'color-shades-generator' && shadeBase ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {generateShades(shadeBase).map((shade) => (
                <button
                  key={shade}
                  onClick={() => handleCopy(shade)}
                  style={{
                    background: shade,
                    height: 56,
                    borderRadius: 8,
                    border: '1px solid rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    fontSize: 10,
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                  }}
                  title={`Copy ${shade}`}
                  type="button"
                >
                  {shade}
                </button>
              ))}
            </div>
          ) : null}
          {variant === 'color-mixer' && mixed ? (
            <>
              <div
                className="tool-live-preview-frame"
                style={{ height: 120, borderRadius: 12, background: rgbToHex(mixed) }}
              />
              <div className="tool-live-field tool-live-field-full">
                <span>Mixed color</span>
                <input
                  onFocus={(e) => e.target.select()}
                  readOnly
                  type="text"
                  value={rgbToHex(mixed)}
                />
              </div>
              <button
                className="button button-secondary"
                onClick={() => handleCopy(rgbToHex(mixed))}
                type="button"
              >
                Copy HEX
              </button>
            </>
          ) : null}
          <p className="tool-live-panel-note">Color math runs locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
